import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
    ChangeDetectorRef,
    DestroyRef,
    Directive, ElementRef, HostBinding, HostListener,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges
} from "@angular/core";
import { EnhancedFormDirective } from "../../../forms/enhanced-form.directive";
import { MatMenuItem } from "@angular/material/menu";
import { Subscription } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ProcessingDirective } from "../processing/processing.directive";
import { HotkeysHostDirective } from "../../../directives/hotkeys/hotkeys-host.directive";
import { PanelTriggerHostDirective } from "../../../directives/panel-trigger/panel-trigger-host.directive";

/**
 * Directive that should be applied to all mat buttons.
 *
 * Will automatically disable when the app state is processing.
 *
 * If optional `enhancedForm` is provided it will disable when the form cannot be submitted
 * (disabled, invalid, or no changes).
 *
 * The `mgdButtonType` input will set the button's appearance.
 *
 * The `clickHotkey` input registers a hotkey combination that programmatically clicks the button
 * when triggered. The click is suppressed if the button is disabled.
 *
 * @example
 * ```html
 * <button mat-raised-button mgdButton mgdButtonType="save" [enhancedForm]="mgdEnhancedForm">Save</button>
 * <button mat-raised-button mgdButton clickHotkey="meta.enter" (click)="onSubmit()">Save</button>
 * ```
 */
@Directive({
    selector: "[mgdButton]",
    hostDirectives: [
        {
            directive: ProcessingDirective,
            inputs: ["disableWhileProcessing", "disabled"]
        },
        HotkeysHostDirective,
        PanelTriggerHostDirective
    ]
})
export class ButtonDirective implements OnInit, OnChanges {

    /**
     * Will stop the click event from propagating to the parent element's click event
     *
     * Default is true. It is never applied on a `mat-menu-item`: the menu closes itself from a click
     * that bubbles up to its panel, so stopping the event would leave the menu open after the item is used.
     *
     * If set to false, the click event will propagate to the parent element's click event
     */
    @Input({ transform: coerceBooleanProperty }) stopClickEventPropagation?: boolean = true;

    /**
     * Will disable when the form is cannot be submitted (disabled, invalid, no changes)
     */
    @Input() enhancedForm?: EnhancedFormDirective;

    /**
     * will set the button's appearance
     */
    @Input("mgdButtonType") buttonType?: "save" | "caution" | "tonal";

    /**
     * When set, registers the given hotkey combination to programmatically click the button.
     * The click is suppressed if the button is disabled.
     *
     * @example
     * ```html
     * <button mat-raised-button mgdButton clickHotkey="meta.enter" (click)="onSubmit()">Save</button>
     * ```
     */
    @Input() clickHotkey?: string;

    /**
     * Add the save button styling class
     */
    @HostBinding("class.mgd-save-btn")
    get saveButton(): boolean {

        return this.buttonType === "save";
    }

    /**
     * Add the caution button styling class
     */
    @HostBinding("class.mgd-caution-btn")
    get cautionButton(): boolean {

        return this.buttonType === "caution";
    }

    /**
     * Add the tonal (soft-filled) button styling class
     */
    @HostBinding("class.mgd-tonal-btn")
    get tonalButton(): boolean {

        return this.buttonType === "tonal";
    }

    /**
     * Tracks the subscription to the enhanced form, so it can be unsubscribed if the input changes
     */
    private _formSubscription?: Subscription;

    /**
     * Tracks the subscription to the hotkey shortcut, so it can be unsubscribed when the hotkey changes
     */
    private _hotkeySubscription?: Subscription;

    /**
     * This reference is used to unsubscribe from all subscriptions when the directive is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Instance of the Processing Directive
     */
    private readonly _processing = inject(ProcessingDirective, { self: true });

    /**
     * Instance of the HotkeysHostDirective composed via hostDirectives
     */
    private readonly _hotkeys = inject(HotkeysHostDirective, { self: true });

    /**
     * Reference to the host button element, used to programmatically trigger clicks via hotkey
     */
    private readonly _elementRef = inject(ElementRef<HTMLButtonElement>);

    /**
     * The host's `mat-menu-item`, when the button is a menu item
     */
    private readonly _menuItem = inject(MatMenuItem, {
        self: true,
        optional: true
    });

    /**
     * Reference to the ChangeDetectorRef service
     */
    private readonly _cdRef = inject(ChangeDetectorRef);

    /**
     * Stop the click event from propagating to the parent's component
     */
    @HostListener("click", ["$event"])
    onClick(event: PointerEvent): void {

        if ((this.stopClickEventPropagation ?? true) && !this._menuItem) {
            event.stopPropagation();
        }
    }

    /**
     * Angular on init lifecycle hook
     *
     * Sets up the app state processing subscription
     *
     */
    ngOnInit(): void {

        this._setButtonDisabled();
        this._registerHotkey();
    }

    /**
     * Angular on changes lifecycle hook
     *
     * Sets up the enhanced form changes subscription
     *
     * Sets the disabled state when the disabled input changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes.enhancedForm) {
            this._formSubscription?.unsubscribe?.();
            if (this.enhancedForm) {
                this._formSubscription = this.enhancedForm.canSubmit$
                    .pipe(takeUntilDestroyed(this._destroyRef))
                    .subscribe(() => {
                        this._setButtonDisabled();
                    });
            }
        }

        if (changes.clickHotkey && !changes.clickHotkey.firstChange) {
            this._registerHotkey();
        }
    }

    /**
     * Sets the button disabled state
     */
    private _setButtonDisabled(): void {

        this._processing.setSelfDisabled(this.enhancedForm && !this.enhancedForm.canSubmit);
        this._cdRef.markForCheck();
    }

    /**
     * Unsubscribes from any existing hotkey subscription and re-registers if `clickHotkey` is set.
     * Called from `ngOnInit` and from `ngOnChanges` when `clickHotkey` changes after init.
     */
    private _registerHotkey(): void {
        this._hotkeySubscription?.unsubscribe();
        this._hotkeySubscription = undefined;

        if (!this.clickHotkey) {
            return;
        }

        this._hotkeySubscription = this._hotkeys.addShortcut$({
            keys: this.clickHotkey,
            allowIn: ["INPUT", "SELECT", "TEXTAREA"]
        })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                if (!this._processing.disabled) {
                    (this._elementRef.nativeElement as HTMLButtonElement).click();
                }
            });
    }
}
